import React, { FunctionComponentElement } from 'react';

import { Root, createRoot } from 'react-dom/client';

interface RenderComponentProps {
    id: string;
    component: FunctionComponentElement<any> | FunctionComponentElement<any>[];
    container: Element;
    props?: any;
}

interface RenderComponentByReferenceProps {
    id: string;
    Component: any;
    container: Element;
    props?: any;
}

/**
 * Store a React Root on the window referenced by a unique container ID
 * once we've rendered it so that we can fetch it again when it's time to unmount
 */
export const pushRoot = (id: string, root: Root): void => {
    window.reactRoots = {
        ...window.reactRoots,
        [id]: root,
    };
};

/**
 * Fetch the React Root on the window by the unique container ID and unmount it
 */
export const popRoot = (id: string): Root => {
    const root = window.reactRoots?.[id];
    delete window.reactRoots?.[id];

    return root;
};

/**
 * Just a wrapper over ReactDOM.unmountComponentAtNode
 */
export function unmountComponentAtNode(id: string): void {
    const root = popRoot(id);

    if (root) {
        root.unmount();
    }
}

/**
 * Just a wrapper over ReactDOM.render to render a component without a store
 */
export const renderComponent = ({ id, component, container }: RenderComponentProps): void => {
    const root = createRoot(container);
    root.render(component);

    pushRoot(id, root);
};

/**
 * Just a wrapper over ReactDOM.render to render a component without a store, passing the component by reference.
 * This allows components to be rendered from outside of jsx|tsx files.
 */
export const renderComponentByReference = ({
    id,
    Component,
    container,
    props,
}: RenderComponentByReferenceProps): void => {
    const root = createRoot(container);
    root.render(<Component {...props} />);

    pushRoot(id, root);
};
